import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { invalidateRecordAuthorization } from './recordAuthorizationQuery'
import type { paths } from '@omnea-app/permissions/permissionsHttpClient/permissionsGeneratedTypes'

/** The records that can be marked confidential: the clearance entities, and documents. */
type ConfidentialRecordEntity = paths['/entities/{entity}/{recordId}/confidentiality']['post']['parameters']['path']['entity']

type UpdateRecordConfidentialityParams = {
  entity: ConfidentialRecordEntity
  recordId: string
  /** The record's own detail keys. Record access is invalidated here, since a flip can revoke the marker's own access. */
  invalidateKeys: QueryKey[]
  /** Shown as a toast on failure; a caller that shows the error inline leaves it out. */
  errorMessage?: string
}

/** Marks or unmarks one record as confidential, for a caller sequencing several marks itself. */
export const updateRecordConfidentiality = async ({
  authorization,
  entity,
  recordId,
  isConfidential,
}: {
  authorization: string
  entity: ConfidentialRecordEntity
  recordId: string
  isConfidential: boolean
}) => {
  const permissionsClient = new PermissionsHttpClient({ authorization })
  const { data, error } = await permissionsClient.POST('/entities/{entity}/{recordId}/confidentiality', {
    params: { path: { entity, recordId } },
    body: { isConfidential },
  })
  throwIfApiError(error)
  return data
}

/** Marks or unmarks one record as confidential. */
export const useUpdateRecordConfidentiality = ({
  entity,
  recordId,
  invalidateKeys,
  errorMessage,
}: UpdateRecordConfidentialityParams) => {
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation({
    mutationFn: async (isConfidential: boolean) =>
      updateRecordConfidentiality({ authorization: await getToken(), entity, recordId, isConfidential }),
    onSuccess: async () => {
      await Promise.all([
        ...invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        invalidateRecordAuthorization(queryClient),
      ])
    },
    onError: () => {
      if (errorMessage != null) enqueueSnackbar(errorMessage, { variant: 'error' })
    },
  })
}
